import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, Button, Pill } from "@bios/ui";
import { MODULES } from "@bios/taxonomies";
import { useApiAuthed, useApiDev } from "../auth.js";
import {
  GeoSelector,
  loadGeoSelection,
  type GeoSelection,
} from "../components/GeoSelector.js";
import type {
  IntelligenceMode,
  IntelligenceResponse,
  IntelligenceEvidence,
  IntelligenceRelatedEntity,
  RankedSignal,
  GapFinding,
  DecisionType,
  ActNowItem,
  DecisionAction,
  Confidence,
  InfluenceMap,
  Originator,
  Amplifier,
  PowerActor,
  OverviewResponse,
} from "../api.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

interface ModeChip {
  key: IntelligenceMode;
  label: string;
  defaultQuestion: string;
}

const MODE_CHIPS: ModeChip[] = [
  {
    key: "priorities",
    label: "Priorities",
    defaultQuestion: "What should I watch next across Black media, capital, education, and workforce?",
  },
  {
    key: "brief",
    label: "Brief",
    defaultQuestion: "Brief me on what's real across all active modules.",
  },
  {
    key: "gaps",
    label: "Gaps",
    defaultQuestion: "Find narrative inflation gaps in the last 30 days.",
  },
  {
    key: "track",
    label: "Track",
    defaultQuestion: "Track Donald Trump across modules.",
  },
  {
    key: "shifts",
    label: "Shifts",
    defaultQuestion: "What changed across all modules in the past week?",
  },
];

const TIME_WINDOWS: Array<{ key: string; label: string }> = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All time" },
];

const MODULE_LIST = Array.isArray(MODULES) ? MODULES : [];
const MODULE_KEYS: string[] = Array.isArray(MODULE_LIST)
  ? MODULE_LIST.map((m) => m.key)
  : [];
const HOME_QUESTION = "What should I focus on right now?";

// Phase 22.6.6: universal array safety helper. Anywhere an API field
// could be undefined, run it through asArray() before .length / .map /
// .join. Equivalent to (Array.isArray(x) ? x : []).
function asArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

// Phase 18: pre-filled suggested queries from spec.
const SUGGESTED_QUERIES: string[] = [
  "What workforce signals are closest to measurable Black impact?",
  "Where are workforce programs failing to produce placement?",
  "Are wages improving for Black communities?",
  "What policy actions led to capital movement?",
  "Where is narrative highest vs real activity?",
  "Show gaps between funding announcements and deployment",
  "Find performative policy signals with no funding or outcomes",
];

export function CommandCenter({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();

  const [question, setQuestion] = useState(HOME_QUESTION);
  const [mode, setMode] = useState<IntelligenceMode | undefined>("priorities");
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [timeWindow, setTimeWindow] = useState("7d");
  const [response, setResponse] = useState<IntelligenceResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  // Phase 21: persistent geo scope.
  const [geo, setGeo] = useState<GeoSelection>(() => loadGeoSelection());
  // Phase 23.4: when arriving via auto-execute (?q=...&auto=1) capture
  // the query so we can show "Showing results for: <query>" until the
  // user edits the textarea.
  const [autoQuery, setAutoQuery] = useState<string | null>(null);
  const [investigatingLabel, setInvestigatingLabel] = useState<string | null>(null);

  // Phase 22.6.6: single safe alias used by every array access against
  // activeModules. Never read activeModules directly below.
  const safeSelectedModules: string[] = asArray(activeModules);

  // Init guard FIRST: ensure activeModules is a populated array before
  // any subsequent effect runs against it.
  useEffect(() => {
    setActiveModules((prev) => {
      const safePrev = asArray(prev);
      return MODULE_KEYS.length > 0 && safePrev.length === 0 ? MODULE_KEYS : safePrev;
    });
  }, []);

  // Debug AFTER guards. Logs MODULE_KEYS once everything is safe to read
  // and exposes both arrays on window for inspection.
  useEffect(() => {
    if (typeof window === "undefined") return;
    console.log("MODULE_KEYS:", MODULE_KEYS);
    (
      window as Window & {
        __MODULES__?: typeof MODULES;
        __MODULE_KEYS__?: string[];
      }
    ).__MODULES__ = MODULES;
    (window as Window & { __MODULE_KEYS__?: string[] }).__MODULE_KEYS__ = MODULE_KEYS;
  }, []);

  function pickMode(chip: ModeChip) {
    setMode(chip.key);
    if (!question.trim() || question === HOME_QUESTION) setQuestion(chip.defaultQuestion);
  }
  function toggleModule(key: string) {
    setActiveModules((prev) => {
      const safePrev = asArray(prev);
      return safePrev.includes(key)
        ? safePrev.filter((m) => m !== key)
        : [...safePrev, key];
    });
  }

  async function ask(opts?: { questionOverride?: string }) {
    const q = (opts?.questionOverride ?? question).trim();
    if (!q) {
      setError("Type a question or pick a mode.");
      return;
    }
    setRunning(true);
    setError(null);
    setResponse(null);
    try {
      const modulesForQuery =
        safeSelectedModules.length > 0 ? safeSelectedModules : MODULE_KEYS;
      const result = await api.intelligence({
        question: q,
        modules: modulesForQuery,
        time_window: timeWindow,
        mode,
        // Phase 21: pass geo when not national so the LLM + system-zero
        // detection see the scope. National = no filter.
        geo_scope: geo.scope === "national" ? undefined : geo.scope,
        geo_state: geo.scope !== "national" ? geo.state : undefined,
        geo_city: geo.scope === "city" ? geo.city : undefined,
      });
      setResponse(result);
    } catch (err) {
      setError(String(err));
    } finally {
      setRunning(false);
    }
  }

  // Phase 15: auto-run the home question on first mount so the surface is
  // never blank. Phase 18.1: when arriving from Overview with ?q=..., run
  // that query instead. The handoff also clears the param so reloading
  // doesn't re-trigger.
  const [searchParams, setSearchParams] = useSearchParams();
  const didAutoRun = useRef(false);
  useEffect(() => {
    if (didAutoRun.current) return;
    didAutoRun.current = true;
    const handoff = searchParams.get("q");
    const signalLabel = searchParams.get("signal");
    const isAuto = searchParams.get("auto") === "1";
    if (handoff && handoff.trim()) {
      setQuestion(handoff);
      if (signalLabel && signalLabel.trim()) setInvestigatingLabel(signalLabel);
      // Phase 23.4: capture auto-execute query for the indicator banner.
      if (isAuto) setAutoQuery(handoff);
      void ask({ questionOverride: handoff });
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      next.delete("auto");
      next.delete("signal");
      setSearchParams(next, { replace: true });
    } else {
      void ask({ questionOverride: HOME_QUESTION });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 23.4: hide the auto-indicator the moment the user edits the
  // textarea or runs a different query.
  useEffect(() => {
    if (autoQuery && question !== autoQuery) setAutoQuery(null);
  }, [question, autoQuery]);

  // Phase 20.2: preemptive mode — always-visible "What you should focus on
  // right now" panel pulled from /v1/overview (cached, ~5min freshness).
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .overview("7d")
      .then((d) => {
        if (alive) setOverview(d);
      })
      .catch(() => {
        /* preemptive panel is optional; silently degrade */
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const moduleListReady = Array.isArray(MODULE_LIST) && MODULE_LIST.length > 0;

  return (
    <div className="bios-command-page">
      <MobileCommandTopBar geo={geo} />

      <div className="bios-page-header bios-command-desktop-header">
        <div>
          <h1 className="bios-page-title">Command Center</h1>
          <p className="bios-page-sub">
            What's real · who is shaping it · so what · what to do.
          </p>
        </div>
        <GeoSelector value={geo} onChange={setGeo} />
      </div>

      {overview && <PreemptivePanel overview={overview} />}

      <Card className="bios-command-input-card">
        <div className="bios-cmd-form">
          {investigatingLabel && (
            <div className="bios-command-context-chip">
              Investigating: <span>{investigatingLabel}</span>
            </div>
          )}
          <textarea
            className="bios-cmd-textarea"
            placeholder="Ask what's real..."
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
          />

          <div className="bios-cmd-row">
            <div className="bios-cmd-row-label">Suggested queries</div>
            <div className="bios-cmd-chips">
              {SUGGESTED_QUERIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="bios-cmd-chip"
                  onClick={() => {
                    setQuestion(q);
                    void ask({ questionOverride: q });
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          <div className="bios-cmd-row">
            <div className="bios-cmd-row-label">Mode</div>
            <div className="bios-cmd-chips">
              {MODE_CHIPS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`bios-cmd-chip${mode === c.key ? " active" : ""}`}
                  onClick={() => pickMode(c)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bios-cmd-row">
            <div className="bios-cmd-row-label">Modules</div>
            <div className="bios-cmd-chips">
              {!moduleListReady ? (
                <span className="bios-cmd-meta">Loading modules...</span>
              ) : (
                MODULE_LIST.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    className={`bios-cmd-chip${
                      safeSelectedModules.includes(m.key) ? " active" : ""
                    }`}
                    onClick={() => toggleModule(m.key)}
                  >
                    {m.key}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="bios-cmd-row">
            <div className="bios-cmd-row-label">Window</div>
            <div className="bios-cmd-chips">
              {TIME_WINDOWS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`bios-cmd-chip${timeWindow === t.key ? " active" : ""}`}
                  onClick={() => setTimeWindow(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bios-cmd-actions">
            <Button
              type="button"
              variant="primary"
              onClick={() => ask()}
              disabled={running}
              className="bios-command-submit"
            >
              {running ? "Asking..." : "Ask"}
            </Button>
          </div>
        </div>
      </Card>

      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13, margin: 0 }}>{error}</p>
        </Card>
      )}

      {autoQuery && (
        <div className="bios-auto-indicator" role="status">
          <span className="bios-auto-indicator-tag">Auto-run</span>
          <span className="bios-auto-indicator-text">
            Showing results for: <strong>{autoQuery}</strong>
          </span>
          <button
            type="button"
            className="bios-auto-indicator-clear"
            onClick={() => {
              setAutoQuery(null);
              setQuestion("");
            }}
            aria-label="Dismiss auto-run indicator"
          >
            ×
          </button>
        </div>
      )}

      {running && !response && <LoadingHero />}
      {!running && !response && !error && <SampleHero />}
      {response && <ResponsePanel response={response} />}
      <CommandBottomNav active="command" />
      <div className="bios-feed-bottomnav-spacer" aria-hidden />
    </div>
  );
}

function MobileCommandTopBar({ geo }: { geo: GeoSelection }) {
  return (
    <div className="bios-command-topbar" aria-label="Command context">
      <Link to="/app" className="bios-command-mark" aria-label="BIOS Signals">
        B
      </Link>
      <div className="bios-command-mode">Command</div>
      {geo.scope !== "national" && <div className="bios-command-geo">{geoLabel(geo)}</div>}
    </div>
  );
}

function CommandBottomNav({
  active,
}: {
  active: "signals" | "command" | "intel" | "search" | "profile";
}) {
  return (
    <nav className="bios-feed-bottomnav bios-command-bottomnav" aria-label="Primary navigation">
      <Link
        to="/app"
        className={`bios-feed-bottomnav-tab${active === "signals" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◎</span>
        <span className="bios-feed-bottomnav-label">Signals</span>
      </Link>
      <Link
        to="/app/command"
        className={`bios-feed-bottomnav-tab${active === "command" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌘</span>
        <span className="bios-feed-bottomnav-label">Command</span>
      </Link>
      <Link
        to="/app/intel"
        className={`bios-feed-bottomnav-tab${active === "intel" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◐</span>
        <span className="bios-feed-bottomnav-label">Intel</span>
      </Link>
      <Link
        to="/app/search"
        className={`bios-feed-bottomnav-tab${active === "search" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌕</span>
        <span className="bios-feed-bottomnav-label">Search</span>
      </Link>
      <Link
        to="/app/profile"
        className={`bios-feed-bottomnav-tab${active === "profile" ? " active" : ""}`}
      >
        <span className="bios-feed-bottomnav-icon" aria-hidden>◌</span>
        <span className="bios-feed-bottomnav-label">Profile</span>
      </Link>
    </nav>
  );
}

function LoadingHero() {
  return (
    <div className="bios-cmd-loading">
      <div className="bios-cmd-loading-pulse" />
      <div className="bios-cmd-loading-text">
        BIOS is analyzing Media, Capital, and Education…
      </div>
      <div className="bios-cmd-loading-sub">
        Cross-referencing entities, weighting sources, ranking signals.
      </div>
    </div>
  );
}

function SampleHero() {
  return (
    <div className="bios-cmd-sample">
      <div className="bios-cmd-sample-tag">Sample</div>
      <div className="bios-cmd-sample-text">
        Engage silent-capital signals before media catches up — capital is moving
        before narrative; act before the framing settles.
      </div>
      <div className="bios-cmd-sample-foot">
        This is a placeholder. Run a query above to get your live priority.
      </div>
    </div>
  );
}

function ResponsePanel({ response }: { response: IntelligenceResponse }) {
  const conf = Math.round(response.confidence.score * 100);
  const relatedModules = response.related_modules ?? [];
  const whatToDo = response.what_to_do ?? [];
  const actNow = response.act_now ?? [];
  const doNotDo = response.do_not_do ?? [];
  const whereImpactIsClosest = response.where_impact_is_closest ?? [];
  const criticalSignals = response.critical_signals ?? [];
  const supportingSignals = response.supporting_signals ?? [];
  const gaps = response.gaps ?? [];
  const relatedEntities = response.related_entities ?? [];
  const evidence = response.evidence ?? [];
  return (
    <div className="bios-cmd-response">
      <MobileResponsePanel
        response={response}
        whatToDo={whatToDo}
        actNow={actNow}
        evidence={evidence}
        gaps={gaps}
        relatedEntities={relatedEntities}
      />
      <div className="bios-cmd-response-desktop">
      {/* TOP PRIORITY — most prominent. Sized to fit above the fold on mobile. */}
      <div className={`bios-cmd-priority bios-cmd-priority-${response.decision_type}`}>
        <div className="bios-cmd-priority-label">Top priority right now</div>
        <div className="bios-cmd-priority-text">{response.top_priority}</div>
        <div className="bios-cmd-priority-meta">
          <DecisionBadge type={response.decision_type} />
          <Pill>{modeLabel(response.mode)}</Pill>
          {relatedModules.length > 0 && (
            <span className="bios-cmd-meta">{relatedModules.join(" · ")}</span>
          )}
          <span className="bios-cmd-meta">{response.time_window}</span>
          <span className="bios-cmd-meta">conf {conf}%</span>
          {response.llm_used ? (
            <span className="bios-cmd-meta">synthesized</span>
          ) : (
            <span className="bios-cmd-meta" style={{ color: "var(--bios-text-faint)" }}>
              retrieval-only
            </span>
          )}
        </div>
      </div>

      {/* MISTAKE TO AVOID — Phase 13 forced positioning */}
      {response.mistake && (
        <div className="bios-cmd-mistake">
          <span className="bios-cmd-mistake-label">Without BIOS:</span>
          <span className="bios-cmd-mistake-text">{response.mistake}</span>
        </div>
      )}

      {/* WHAT TO DO — bold, numbered, immediately under priority */}
      {whatToDo.length > 0 && (
        <Card title="What to do">
          <ol className="bios-cmd-do-ol">
            {whatToDo.map((a, i) => (
              <li key={i} className="bios-cmd-do-item">
                <div className="bios-cmd-do-head">
                  <span className="bios-cmd-do-num">{i + 1}</span>
                  <UrgencyChip urgency={a.urgency} />
                  {a.target_entity && a.target_entity !== "—" && (
                    <span className="bios-cmd-action-target">{a.target_entity}</span>
                  )}
                  {(a.modules ?? []).length > 0 && (
                    <span className="bios-cmd-action-modules">{(a.modules ?? []).join(" · ")}</span>
                  )}
                </div>
                <div className="bios-cmd-do-text">{a.action}</div>
                {a.reason && <div className="bios-cmd-do-why">{a.reason}</div>}
                {(a.evidence_ids ?? []).length > 0 && (
                  <div className="bios-cmd-action-evidence">
                    evidence: {(a.evidence_ids ?? []).map((e) => e.slice(0, 8)).join(", ")}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </Card>
      )}

      {/* ACT NOW — strong card */}
      {actNow.length > 0 && (
        <div className="bios-cmd-actnow-strong">
          <div className="bios-cmd-actnow-strong-head">
            <span className="bios-cmd-actnow-strong-label">⚡ Act now</span>
            <span className="bios-cmd-actnow-strong-count">{actNow.length}</span>
          </div>
          <div className="bios-cmd-actnow-list">
            {actNow.map((a, i) => (
              <ActNowCard key={`${a.entity}-${i}`} item={a} />
            ))}
          </div>
        </div>
      )}

      {/* DO NOT — red-tinted, paired with what-to-do */}
      {doNotDo.length > 0 && (
        <Card>
          <div className="bios-cmd-donot-head">Do NOT</div>
          <ol className="bios-cmd-do-ol bios-cmd-donot-ol">
            {doNotDo.map((a, i) => (
              <li key={i} className="bios-cmd-do-item bios-cmd-donot-item">
                <div className="bios-cmd-do-head">
                  <span className="bios-cmd-do-num bios-cmd-do-num-neg">{i + 1}</span>
                  <UrgencyChip urgency={a.urgency} />
                  {a.target_entity && a.target_entity !== "—" && (
                    <span className="bios-cmd-action-target">{a.target_entity}</span>
                  )}
                  {(a.modules ?? []).length > 0 && (
                    <span className="bios-cmd-action-modules">{(a.modules ?? []).join(" · ")}</span>
                  )}
                </div>
                <div className="bios-cmd-do-text">{a.action}</div>
                {a.reason && <div className="bios-cmd-do-why">{a.reason}</div>}
              </li>
            ))}
          </ol>
        </Card>
      )}

      {/* Compact context — lower priority, smaller text */}
      <Card title="Context">
        <CompactSection label="What's real">{response.whats_real || "—"}</CompactSection>
        <CompactSection label="So what">{response.so_what || "—"}</CompactSection>
        <InfluenceMapSection map={response.who_is_shaping_it} />
      </Card>

      {whereImpactIsClosest.length > 0 && (
        <Card title="Where impact is closest">
          <div className="bios-cmd-rank-list">
            {whereImpactIsClosest.map((item) => (
              <div key={item.id} className="bios-cmd-signal">
                <div className="bios-cmd-signal-head">
                  <span className="bios-cmd-signal-modules">{item.module}</span>
                  {item.impact_state && (
                    <span className="bios-cmd-signal-modules">{item.impact_state}</span>
                  )}
                  {item.impact_proximity && (
                    <span className="bios-cmd-signal-modules">
                      {item.impact_proximity}
                    </span>
                  )}
                </div>
                <div className="bios-cmd-signal-title">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noreferrer">
                      {item.title}
                    </a>
                  ) : (
                    item.title
                  )}
                </div>
                <div className="bios-cmd-signal-detail">{item.reason}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ConfidenceCard confidence={response.confidence} />

      {/* Supplemental: ranked signals + gaps + entities + evidence */}
      {(criticalSignals.length > 0 || supportingSignals.length > 0) && (
        <Card title="Ranked signals">
          {criticalSignals.length > 0 && (
            <div className="bios-cmd-rank-group">
              <div className="bios-cmd-rank-label bios-cmd-rank-critical">Critical</div>
              <div className="bios-cmd-rank-list">
                {criticalSignals.map((s) => (
                  <SignalRow key={s.source_id} signal={s} />
                ))}
              </div>
            </div>
          )}
          {supportingSignals.length > 0 && (
            <div className="bios-cmd-rank-group">
              <div className="bios-cmd-rank-label">Supporting</div>
              <div className="bios-cmd-rank-list">
                {supportingSignals.map((s) => (
                  <SignalRow key={s.source_id} signal={s} />
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {gaps.length > 0 && (
        <Card title={`Gap findings (${gaps.length})`}>
          <div className="bios-cmd-gaps">
            {gaps.map((g) => (
              <GapRow key={`${g.kind}:${g.entity_id ?? g.item_id ?? g.label}`} gap={g} />
            ))}
          </div>
        </Card>
      )}

      {relatedEntities.length > 0 && (
        <Card title={`Related entities (${relatedEntities.length})`}>
          <div className="bios-cmd-entities">
            {relatedEntities.map((e) => (
              <EntityCardChip key={e.id} entity={e} />
            ))}
          </div>
        </Card>
      )}

      {evidence.length > 0 && (
        <Card title={`Evidence (${evidence.length})`}>
          <div className="bios-cmd-evidence">
            {evidence.map((ev) => (
              <EvidenceRow key={`${ev.type}:${ev.id}`} evidence={ev} />
            ))}
          </div>
        </Card>
      )}
      </div>
    </div>
  );
}

function MobileResponsePanel({
  response,
  whatToDo,
  actNow,
  evidence,
  gaps,
  relatedEntities,
}: {
  response: IntelligenceResponse;
  whatToDo: DecisionAction[];
  actNow: ActNowItem[];
  evidence: IntelligenceEvidence[];
  gaps: GapFinding[];
  relatedEntities: IntelligenceRelatedEntity[];
}) {
  const nextActions = [
    ...whatToDo.map((action, i) => ({
      id: `todo-${i}`,
      title: action.action,
      reason: action.reason,
      modules: action.modules ?? [],
    })),
    ...actNow.map((item, i) => ({
      id: `act-${i}`,
      title: item.recommended_action || item.title,
      reason: item.reason,
      modules: item.modules ?? [],
    })),
  ].slice(0, 3);

  return (
    <div className="bios-command-mobile-response">
      <section className="bios-command-answer" aria-label="Answer">
        <div className="bios-command-section-label">Answer</div>
        <p>{directAnswer(response)}</p>
      </section>

      {nextActions.length > 0 && (
        <section className="bios-command-next" aria-label="Do next">
          <div className="bios-command-section-label">Do next</div>
          <div className="bios-command-action-list">
            {nextActions.map((action) => (
              <article key={action.id} className="bios-command-action-card">
                <div className="bios-command-action-title">{verbFirst(action.title)}</div>
                {action.reason && (
                  <div className="bios-command-action-reason">{oneLine(action.reason)}</div>
                )}
                {action.modules.length > 0 && (
                  <div className="bios-command-action-modules">
                    {action.modules.slice(0, 2).map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {evidence.length > 0 && (
        <details className="bios-command-details">
          <summary>Evidence ({evidence.length})</summary>
          <div className="bios-cmd-evidence">
            {evidence.map((ev) => (
              <EvidenceRow key={`${ev.type}:${ev.id}`} evidence={ev} />
            ))}
          </div>
        </details>
      )}

      {gaps.length > 0 && (
        <details className="bios-command-details">
          <summary>Gaps ({gaps.length})</summary>
          <div className="bios-cmd-gaps">
            {gaps.map((g) => (
              <GapRow key={`${g.kind}:${g.entity_id ?? g.item_id ?? g.label}`} gap={g} />
            ))}
          </div>
        </details>
      )}

      {relatedEntities.length > 0 && (
        <details className="bios-command-details">
          <summary>Entities ({relatedEntities.length})</summary>
          <div className="bios-cmd-entities">
            {relatedEntities.map((e) => (
              <EntityCardChip key={e.id} entity={e} />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function InfluenceMapSection({ map }: { map: InfluenceMap }) {
  const originators = map.originators ?? [];
  const amplifiers = map.amplifiers ?? [];
  const powerActors = map.power_actors ?? [];
  return (
    <div className="bios-cmd-section">
      <div className="bios-cmd-section-label">Who is shaping it</div>
      {map.summary && (
        <div className="bios-cmd-section-body" style={{ marginBottom: 8 }}>
          {map.summary}
        </div>
      )}
      <div className="bios-cmd-influence-grid">
        <InfluenceGroup label="Originators" empty="No primary-tier originators in window.">
          {originators.map((o, i) => (
            <OriginatorRow key={i} item={o} />
          ))}
        </InfluenceGroup>
        <InfluenceGroup label="Amplifiers" empty="No amplifier-pattern sources in window.">
          {amplifiers.map((a, i) => (
            <AmplifierRow key={i} item={a} />
          ))}
        </InfluenceGroup>
        <InfluenceGroup label="Power actors" empty="No typed power actors in window.">
          {powerActors.map((p, i) => (
            <PowerActorRow key={i} item={p} />
          ))}
        </InfluenceGroup>
      </div>
    </div>
  );
}

function InfluenceGroup({
  label,
  children,
  empty,
}: {
  label: string;
  children: React.ReactNode;
  empty: string;
}) {
  const arr = Array.isArray(children) ? children : [children];
  const hasItems = arr.filter(Boolean).length > 0;
  return (
    <div className="bios-cmd-influence-group">
      <div className="bios-cmd-influence-group-label">{label}</div>
      {hasItems ? (
        <div className="bios-cmd-influence-list">{children}</div>
      ) : (
        <div className="bios-cmd-influence-empty">{empty}</div>
      )}
    </div>
  );
}

function OriginatorRow({ item }: { item: Originator }) {
  return (
    <div className="bios-cmd-influence-item">
      <div className="bios-cmd-influence-name">{item.name}</div>
      <div className="bios-cmd-influence-meta">
        <span>{item.module}</span>
        <span>·</span>
        <span>{item.tier}</span>
        <span>·</span>
        <span>weight {(item.weight * 100).toFixed(0)}</span>
      </div>
      <div className="bios-cmd-influence-reason">{item.reason}</div>
    </div>
  );
}

function AmplifierRow({ item }: { item: Amplifier }) {
  return (
    <div className="bios-cmd-influence-item">
      <div className="bios-cmd-influence-name">{item.name}</div>
      <div className="bios-cmd-influence-meta">
        <span>{item.module}</span>
        <span>·</span>
        <span>{item.item_count} items</span>
        <span>·</span>
        <span>reality {(item.avg_reality * 100).toFixed(0)}/100</span>
      </div>
      <div className="bios-cmd-influence-reason">{item.reason}</div>
    </div>
  );
}

function PowerActorRow({ item }: { item: PowerActor }) {
  const modules = item.modules ?? [];
  return (
    <div className="bios-cmd-influence-item">
      <div className="bios-cmd-influence-name">{item.name}</div>
      <div className="bios-cmd-influence-meta">
        <span>{item.type}</span>
        {item.subtype && (
          <>
            <span>·</span>
            <span>{item.subtype}</span>
          </>
        )}
        <span>·</span>
        <span>{item.mention_count} mentions</span>
        {modules.length > 0 && (
          <>
            <span>·</span>
            <span>{modules.join("/")}</span>
          </>
        )}
      </div>
      <div className="bios-cmd-influence-reason">{item.reason}</div>
    </div>
  );
}

function CompactSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bios-cmd-compact">
      <div className="bios-cmd-compact-label">{label}</div>
      <div className="bios-cmd-compact-body">{children}</div>
    </div>
  );
}

function UrgencyChip({ urgency }: { urgency: "HIGH" | "MEDIUM" | "LOW" }) {
  const color =
    urgency === "HIGH"
      ? "var(--bios-noise)"
      : urgency === "MEDIUM"
      ? "var(--bios-mixed)"
      : "var(--bios-text-faint)";
  return (
    <span className="bios-cmd-urgency" style={{ color, borderColor: color }}>
      {urgency}
    </span>
  );
}

function ActNowCard({ item }: { item: ActNowItem }) {
  const conf = Math.round(item.confidence * 100);
  const modules = item.modules ?? [];
  return (
    <div className="bios-cmd-actnow">
      <div className="bios-cmd-actnow-head">
        <DecisionBadge type={item.decision_kind} />
        <span className="bios-cmd-actnow-conf" style={{ color: scoreColor(conf) }}>
          {conf}%
        </span>
      </div>
      <div className="bios-cmd-actnow-title">{item.title}</div>
      {item.entity && <div className="bios-cmd-actnow-entity">{item.entity}</div>}
      {modules.length > 0 && (
        <div className="bios-cmd-actnow-modules">{modules.join(" · ")}</div>
      )}
      <div className="bios-cmd-actnow-reason">
        <span className="bios-cmd-actnow-label">Why</span>
        {item.reason}
      </div>
      <div className="bios-cmd-actnow-action">
        <span className="bios-cmd-actnow-label">Recommended action</span>
        {item.recommended_action}
      </div>
    </div>
  );
}

function ConfidenceCard({ confidence }: { confidence: Confidence }) {
  const pct = Math.round(confidence.score * 100);
  const drivers = confidence.drivers ?? [];
  const limitations = confidence.limitations ?? [];
  return (
    <Card title={`Confidence: ${pct}%`}>
      <div className="bios-cmd-conf">
        <div className="bios-cmd-conf-block">
          <div className="bios-cmd-conf-label">Drivers</div>
          {drivers.length === 0 ? (
            <div className="bios-cmd-conf-empty">—</div>
          ) : (
            <ul className="bios-cmd-conf-list">
              {drivers.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="bios-cmd-conf-block">
          <div className="bios-cmd-conf-label">Limitations</div>
          {limitations.length === 0 ? (
            <div className="bios-cmd-conf-empty">—</div>
          ) : (
            <ul className="bios-cmd-conf-list">
              {limitations.map((l, i) => (
                <li key={i}>{l}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function SignalRow({ signal }: { signal: RankedSignal }) {
  const score = Math.round(signal.rank_score * 100);
  const modules = signal.modules ?? [];
  return (
    <div className="bios-cmd-signal">
      <div className="bios-cmd-signal-head">
        <span className="bios-cmd-signal-kind">{signal.source_kind}</span>
        {modules.length > 0 && (
          <span className="bios-cmd-signal-modules">{modules.join(" · ")}</span>
        )}
        <span className="bios-cmd-signal-score" style={{ color: scoreColor(score) }}>
          {score}
        </span>
      </div>
      <div className="bios-cmd-signal-title">{signal.title}</div>
      {signal.detail && <div className="bios-cmd-signal-detail">{signal.detail}</div>}
      <div className="bios-cmd-signal-rationale">{signal.rationale}</div>
    </div>
  );
}

function GapRow({ gap }: { gap: GapFinding }) {
  return (
    <div className="bios-cmd-gap">
      <div className="bios-cmd-gap-head">
        <span className="bios-cmd-gap-kind">{gap.kind.replace(/_/g, " ")}</span>
        {gap.module && <span className="bios-cmd-gap-module">{gap.module}</span>}
      </div>
      <div className="bios-cmd-gap-title">{gap.label}</div>
      <div className="bios-cmd-gap-detail">{gap.detail}</div>
    </div>
  );
}

function EntityCardChip({ entity }: { entity: IntelligenceRelatedEntity }) {
  const modules = entity.modules ?? [];
  return (
    <div className="bios-cmd-entity">
      <div className="bios-cmd-entity-name">{entity.name}</div>
      <div className="bios-cmd-entity-meta">
        <span>{entity.type}</span>
        <span>·</span>
        <span>{entity.mention_count} mentions</span>
        {modules.length > 0 && (
          <>
            <span>·</span>
            <span>{modules.join(" / ")}</span>
          </>
        )}
      </div>
    </div>
  );
}

function EvidenceRow({ evidence }: { evidence: IntelligenceEvidence }) {
  const score =
    typeof evidence.reality_score === "number" ? Math.round(evidence.reality_score * 100) : null;
  return (
    <div className="bios-cmd-ev-row">
      <div className="bios-cmd-ev-head">
        <span className="bios-cmd-ev-type">{evidence.type}</span>
        {evidence.module && <span className="bios-cmd-ev-module">{evidence.module}</span>}
        {evidence.signal_type && (
          <span className="bios-cmd-ev-signal">{evidence.signal_type}</span>
        )}
        {score !== null && (
          <span className="bios-cmd-ev-score" style={{ color: scoreColor(score) }}>
            {score}
          </span>
        )}
      </div>
      <div className="bios-cmd-ev-title">
        {evidence.url ? (
          <a href={evidence.url} target="_blank" rel="noreferrer">
            {evidence.title}
          </a>
        ) : (
          evidence.title
        )}
      </div>
      {evidence.detail && <div className="bios-cmd-ev-detail">{evidence.detail}</div>}
    </div>
  );
}

function directAnswer(response: IntelligenceResponse): string {
  const first = firstSentence(response.top_priority || response.whats_real || "");
  const second = firstSentence(response.so_what || "");
  return [first, second].filter(Boolean).join(" ");
}

function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const match = trimmed.match(/^(.+?[.!?])(?:\s|$)/);
  return match?.[1] ?? trimmed;
}

function oneLine(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function verbFirst(text: string): string {
  const cleaned = oneLine(text);
  return cleaned.replace(/^(you should|we should|the user should|bios should)\s+/i, "");
}

const STATE_LABELS: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

function geoLabel(geo: GeoSelection): string {
  if (geo.scope === "city" && geo.city) return geo.city;
  if (geo.scope === "state" && geo.state) return STATE_LABELS[geo.state] ?? geo.state;
  return "National";
}

function modeLabel(m: IntelligenceMode): string {
  switch (m) {
    case "brief":
      return "Brief";
    case "priorities":
      return "Priorities";
    case "gaps":
      return "Gaps";
    case "track":
      return "Track";
    case "shifts":
      return "Shifts";
    default:
      return String(m);
  }
}

const DECISION_BADGE: Record<DecisionType, { color: string; bg: string; label: string }> = {
  OPPORTUNITY: { color: "#0a3819", bg: "var(--bios-signal)", label: "OPPORTUNITY" },
  RISK: { color: "#3b0d0d", bg: "var(--bios-noise)", label: "RISK" },
  MISDIRECTION: { color: "#3a2607", bg: "var(--bios-mixed)", label: "MISDIRECTION" },
  EMERGING_TREND: { color: "#0b1e3a", bg: "var(--bios-info)", label: "EMERGING TREND" },
  WATCHLIST: { color: "var(--bios-text)", bg: "var(--bios-bg-elev-2)", label: "WATCHLIST" },
};

function DecisionBadge({ type }: { type: DecisionType }) {
  const cfg = DECISION_BADGE[type] ?? DECISION_BADGE.WATCHLIST;
  return (
    <span
      className="bios-cmd-decision-badge"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.label}
    </span>
  );
}

function scoreColor(score: number): string {
  if (score >= 60) return "var(--bios-signal)";
  if (score >= 35) return "var(--bios-mixed)";
  return "var(--bios-noise)";
}

// Phase 20.4: PreemptivePanel structure —
//   1. Urgency badge
//   2. Headline truth (or interpretation when impact > 0)
//   3. Scope of failure
//   4. Accountability summary
//   5. Why (root causes)
//   6. System causality (root failure mode + 1-2 links)
//   7. Immediate priorities
//   8. Consequences if unchanged
function PreemptivePanel({ overview }: { overview: OverviewResponse }) {
  const urgency = overview.urgency;
  const causes = asArray(overview.root_causes);
  // Phase 22.6.6: focus_recommendations could be undefined when an old
  // cached payload arrives. Guard before .slice().
  const recs = asArray(overview.focus_recommendations).slice(0, 3);
  const headlineTruth = overview.headline_truth ?? null;
  const scope = overview.scope_of_failure ?? null;
  const interp = overview.interpretation;
  const accountability = overview.accountability;
  const causality = overview.system_causality;
  const projection = overview.forward_projection;
  const causalityLinks = asArray(causality?.system_links);
  const consequences = asArray(projection?.consequences);

  if (!headlineTruth && !interp && causes.length === 0 && recs.length === 0) {
    return null;
  }

  return (
    <div
      className={`bios-preempt${urgency ? ` bios-preempt-${urgency.level}` : ""}`}
    >
      <div className="bios-preempt-eyebrow">What you should focus on right now</div>

      {urgency && urgency.level !== "low" && (
        <div className={`bios-preempt-urgency bios-preempt-urgency-${urgency.level}`}>
          {urgency.level === "critical" ? "Critical" : "Elevated"}
        </div>
      )}

      {headlineTruth ? (
        <>
          <div className="bios-preempt-truth">{headlineTruth}</div>
          {scope && <div className="bios-preempt-scope">{scope}</div>}
        </>
      ) : interp ? (
        <>
          <div className="bios-preempt-truth">{interp.headline}</div>
          {scope && <div className="bios-preempt-scope">{scope}</div>}
          {interp.subline && (
            <div className="bios-preempt-sub">{interp.subline}</div>
          )}
        </>
      ) : null}

      {accountability?.summary && (
        <div className="bios-preempt-section">
          <div className="bios-preempt-section-label">Accountability</div>
          <div className="bios-preempt-accountability">
            {accountability.summary}
          </div>
        </div>
      )}

      {causes.length > 0 && (
        <div className="bios-preempt-section">
          <div className="bios-preempt-section-label">Why</div>
          <ul className="bios-preempt-list">
            {causes.map((c, i) => (
              <li key={i} className="bios-preempt-cause">
                {c.cause}
              </li>
            ))}
          </ul>
        </div>
      )}

      {causality && (causality.root_failure_mode || causalityLinks.length > 0) && (
        <div className="bios-preempt-section">
          <div className="bios-preempt-section-label">System causality</div>
          {causality.root_failure_mode && (
            <div className="bios-preempt-causality-root">
              {causality.root_failure_mode}
            </div>
          )}
          {causalityLinks.length > 0 && (
            <ul className="bios-preempt-list">
              {causalityLinks.slice(0, 2).map((l, i) => (
                <li key={i} className="bios-preempt-cause">
                  {l.statement}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {recs.length > 0 && (
        <div className="bios-preempt-section">
          <div className="bios-preempt-section-label">Immediate priorities</div>
          <ol className="bios-preempt-priorities">
            {recs.map((r, i) => (
              <li key={i} className="bios-preempt-priority">
                <Link
                  to={`/app/command?q=${encodeURIComponent(r.query)}`}
                  className="bios-preempt-priority-link"
                >
                  <span className="bios-preempt-priority-num">{i + 1}</span>
                  <span className="bios-preempt-priority-label">{r.label}</span>
                  <span className="bios-preempt-priority-arrow" aria-hidden>→</span>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      )}

      {consequences.length > 0 && (
        <div className="bios-preempt-section bios-preempt-consequences">
          <div className="bios-preempt-section-label">If nothing changes</div>
          <ul className="bios-preempt-list">
            {consequences.map((c, i) => (
              <li key={i} className="bios-preempt-consequence">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
